import React, { useState, useEffect } from 'react';
import HostNav from '../components/hostComponents/HostNav';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import request from '../utils/request';
import { ToastContainer, toast } from 'react-toastify';
import { getFlags, updateEntryInstructionEmailPrefs } from '../store/thunks/hostActions';
import { setHostInfo } from '../store/slices/hostSlices';

// ─── PMS Status Card ─────────────────────────────────────────────────────────
function PMSStatusCard({ title, status, isConnected, onConnect, onDisconnect, onSync, loading, icon, gradient, children }) {
  const statusConfig = {
    connected: {
      badge: 'bg-gradient-to-r from-green-500 to-emerald-500 text-white',
      dot: 'bg-green-400',
      label: 'Connected',
    },
    disconnected: {
      badge: 'bg-gradient-to-r from-gray-400 to-gray-500 text-white',
      dot: 'bg-gray-400',
      label: 'Disconnected',
    },
    'needs-setup': {
      badge: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white',
      dot: 'bg-amber-400',
      label: 'Needs Setup',
    },
    syncing: {
      badge: 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white',
      dot: 'bg-blue-400 animate-pulse',
      label: 'Syncing',
    },
  };

  const config = statusConfig[status] || statusConfig.disconnected;

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="p-6 border-b border-gray-100/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 bg-gradient-to-br ${gradient || 'from-primary-500 to-purple-600'} rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20`}>
              {icon || (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              <div className="flex items-center space-x-2 mt-1">
                <div className={`w-2 h-2 rounded-full ${config.dot}`}></div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${config.badge}`}>
                  {config.label}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isConnected ? (
              <>
                {onSync && (
                  <button
                    onClick={onSync}
                    disabled={loading}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 font-medium text-sm disabled:opacity-50 shadow-md shadow-blue-500/20 hover:shadow-lg hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <span className="flex items-center space-x-1.5">
                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span>Syncing...</span>
                      </span>
                    ) : 'Sync Now'}
                  </button>
                )}
                <button
                  onClick={onDisconnect}
                  disabled={loading}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl hover:from-red-600 hover:to-rose-700 transition-all duration-300 font-medium text-sm disabled:opacity-50 shadow-md shadow-red-500/20 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={onConnect}
                className="px-5 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 font-medium text-sm shadow-lg shadow-primary-500/25 hover:shadow-xl hover:-translate-y-0.5"
              >
                Connect
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function APIKeyInput({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-4 py-3 border border-gray-200/60 rounded-xl bg-white/80 backdrop-blur-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-200 text-sm"
      />
    </div>
  );
}

const INTEGRATIONS_TOAST_STORAGE_KEY = 'pending_integration_toast';

const normalizePmsSelections = (raw) => {
  const list = Array.isArray(raw) ? raw : (typeof raw === 'string' ? [raw] : []);
  return list.map((item) => String(item || '').trim().toLowerCase()).filter(Boolean);
};

const IntegrationsScreen = () => {
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);
  const {
    receiveEntryInstructionsEmails,
    entryInstructionsAdditionalEmails = [],
    loading: loadingFlags,
  } = useSelector((state) => state.hostFlags);

  const [loading, setLoading] = useState(false);
  const [newNotificationEmail, setNewNotificationEmail] = useState('');
  const [entryEmailError, setEntryEmailError] = useState('');
  const [entryEmailSuccess, setEntryEmailSuccess] = useState('');

  useEffect(() => {
    const pendingToastRaw = sessionStorage.getItem(INTEGRATIONS_TOAST_STORAGE_KEY);
    if (!pendingToastRaw) return;
    sessionStorage.removeItem(INTEGRATIONS_TOAST_STORAGE_KEY);
    try {
      const pendingToast = JSON.parse(pendingToastRaw);
      if (pendingToast?.type === 'success') {
        toast.success(pendingToast.message || 'Integration updated.');
      } else if (pendingToast?.type === 'error') {
        toast.error(pendingToast.message || 'Integration update failed.');
      }
    } catch (error) {
      // Ignore malformed session values.
    }
  }, []);

  useEffect(() => {
    if (hostInfo?._id) dispatch(getFlags(hostInfo._id));
  }, [dispatch, hostInfo?._id]);
  const [showGuestyForm, setShowGuestyForm] = useState(false);
  const [showStreamlineForm, setShowStreamlineForm] = useState(false);
  const [guestyToken, setGuestyToken] = useState('');
  const [streamlineApiKey, setStreamlineApiKey] = useState('');
  const [streamlineApiSecret, setStreamlineApiSecret] = useState('');

  const pmsSelections = normalizePmsSelections(hostInfo?.propertyManagementSoftware);
  const hasConfiguredPms = pmsSelections.length > 0;
  const showGuestyCard = hasConfiguredPms ? pmsSelections.includes('guesty') : true;
  const showStreamlineCard = hasConfiguredPms ? pmsSelections.includes('streamline') : true;
  const showHospitableCard = pmsSelections.includes('hospitable');

  const isGuestyConnected = hostInfo?.guesty_client_id && hostInfo?.guesty_client_secret;
  const isStreamlineConnected = hostInfo?.streamline_api_key && hostInfo?.streamline_api_secret;
  const isHospitableConnected = Boolean(hostInfo?.hospitableConnectedAt || hostInfo?.hospitableAccountId);
  const isStreamlineHost = pmsSelections.includes('streamline');
  const needsStreamlineKeys = isStreamlineHost && !isStreamlineConnected;

  const mergeHostInfo = (patch) => {
    if (!hostInfo) return;
    const nextHostInfo = { ...hostInfo, ...patch };
    localStorage.setItem("hostInfo", JSON.stringify(nextHostInfo));
    dispatch(setHostInfo(nextHostInfo));
  };

  const refreshHostInfo = async () => {
    if (!hostInfo?._id) return;
    const { data } = await request.get(`/hosts/${hostInfo._id}`, { withCredentials: true });
    localStorage.setItem("hostInfo", JSON.stringify(data));
    dispatch(setHostInfo(data));
  };

  const handleGuestyConnect = async () => {
    if (!guestyToken) {
      toast.error('Please enter a Guesty token');
      return;
    }
    setLoading(true);
    try {
      const response = await request.post(
        `/hosts/save-guesty-status/${hostInfo._id}`,
        { token: guestyToken },
        { withCredentials: true }
      );
      if (response.data.success) {
        toast.success(response.data.msg || 'Guesty connected successfully!');
        setGuestyToken('');
        setShowGuestyForm(false);
        mergeHostInfo({
          guesty_client_id: response.data.guesty_client_id,
          guesty_client_secret: response.data.guesty_client_secret,
        });
      } else {
        toast.error(response.data.msg || 'Failed to connect to Guesty');
      }
    } catch (error) {
      if (error.response?.data?.message === 'Unauthenticated' || error.response?.status === 401) {
        toast.error('The token is invalid or expired. Please check and try again.');
      } else {
        toast.error(error.response?.data?.message || error.response?.data || error.message || 'An unexpected error occurred.');
      }
    }
    setLoading(false);
  };

  const handleStreamlineConnect = async () => {
    if (!streamlineApiKey || !streamlineApiSecret) {
      toast.error('Please enter both API Key and Secret');
      return;
    }
    setLoading(true);
    try {
      const response = await request.post(
        `/hosts/save-streamline-status/${hostInfo._id}`,
        { apiKey: streamlineApiKey, apiSecret: streamlineApiSecret },
        { withCredentials: true }
      );
      if (response.data.success) {
        toast.success(response.data.msg || 'Streamline connected successfully!');
        setStreamlineApiKey('');
        setStreamlineApiSecret('');
        setShowStreamlineForm(false);
        mergeHostInfo({
          streamline_api_key: response.data.streamline_api_key,
          streamline_api_secret: response.data.streamline_api_secret,
        });
      } else {
        toast.error(response.data.msg || 'Failed to connect to Streamline');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || 'An unexpected error occurred.');
    }
    setLoading(false);
  };

  const handleGuestyDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect from Guesty?")) return;
    setLoading(true);
    try {
      const response = await request.get(`/hosts/disconnect-guesty/${hostInfo._id}`, { withCredentials: true });
      if (response.data.success) {
        toast.success(response.data.msg || 'Guesty disconnected successfully!');
        mergeHostInfo({
          guesty_client_id: null,
          guesty_client_secret: null,
        });
        window.location.reload();
      } else {
        toast.error(response.data.msg || 'Failed to disconnect from Guesty');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || 'An unexpected error occurred.');
    }
    setLoading(false);
  };

  const handleStreamlineDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect from Streamline?")) return;
    setLoading(true);
    try {
      const response = await request.get(`/hosts/disconnect-streamline/${hostInfo._id}`, { withCredentials: true });
      if (response.data.success) {
        toast.success(response.data.msg || 'Streamline disconnected successfully!');
        mergeHostInfo({
          streamline_api_key: null,
          streamline_api_secret: null,
        });
        window.location.reload();
      } else {
        toast.error(response.data.msg || 'Failed to disconnect from Streamline');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || 'An unexpected error occurred.');
    }
    setLoading(false);
  };

  const handleGuestySync = async () => {
    setLoading(true);
    try {
      const response = await request.post(`/hosts/sync-guesty-properties/${hostInfo._id}`, {}, { withCredentials: true });
      toast.success(response.data.msg || 'Guesty properties synced successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || 'Failed to sync Guesty properties');
    }
    setLoading(false);
  };

  const handleStreamlineSync = async () => {
    setLoading(true);
    try {
      const response = await request.post(`/hosts/sync-streamline-properties/${hostInfo._id}`, {}, { withCredentials: true });
      toast.success(response.data.msg || 'Streamline properties synced successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || 'Failed to sync Streamline properties');
    }
    setLoading(false);
  };

  const handleHospitableConnect = async () => {
    setLoading(true);
    try {
      const response = await request.get(`/hosts/hospitable/oauth-url/${hostInfo._id}`, { withCredentials: true });
      if (response?.data?.success && response?.data?.oauthUrl) {
        window.location.assign(response.data.oauthUrl);
        return;
      }
      toast.error(response?.data?.message || 'Failed to start Hospitable connection');
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || 'Failed to start Hospitable connection');
    }
    setLoading(false);
  };

  const handleHospitableSync = async () => {
    setLoading(true);
    try {
      const response = await request.post(`/hosts/sync-hospitable-properties/${hostInfo._id}`, {}, { withCredentials: true });
      const summary = response?.data?.summary;
      if (summary) {
        toast.success(
          `${response.data.msg || 'Hospitable sync complete.'} Created: ${summary.created || 0}, Updated: ${summary.updated || 0}, Skipped: ${summary.skipped || 0}, Failed: ${summary.failed || 0}`
        );
      } else {
        toast.success(response.data.msg || 'Hospitable properties synced successfully!');
      }
      await refreshHostInfo();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || 'Failed to sync Hospitable properties');
    }
    setLoading(false);
  };

  const handleHospitableDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect from Hospitable?")) return;
    setLoading(true);
    try {
      const response = await request.get(`/hosts/disconnect-hospitable/${hostInfo._id}`, { withCredentials: true });
      if (response.data.success) {
        toast.success(response.data.msg || 'Hospitable disconnected successfully!');
        mergeHostInfo({
          hospitableAccountId: null,
          hospitableConnectedAt: null,
        });
      } else {
        toast.error(response.data.msg || 'Failed to disconnect from Hospitable');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || 'An unexpected error occurred.');
    }
    setLoading(false);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());

  const handleToggleEntryInstructions = () => {
    setEntryEmailError('');
    setEntryEmailSuccess('');
    dispatch(updateEntryInstructionEmailPrefs({
      hid: hostInfo._id,
      receiveEntryInstructionsEmails: !receiveEntryInstructionsEmails,
      entryInstructionsAdditionalEmails,
    })).then((result) => {
      if (result.meta.requestStatus === 'fulfilled' && result.payload?.success) {
        setEntryEmailSuccess(result.payload.msg || 'Saved.');
      } else if (result.meta.requestStatus === 'rejected' && result.payload?.error) {
        setEntryEmailError(result.payload.error);
      }
    });
  };

  const handleAddNotificationEmail = () => {
    const email = newNotificationEmail.trim();
    setEntryEmailError('');
    setEntryEmailSuccess('');
    if (!email) {
      setEntryEmailError('Enter an email address.');
      return;
    }
    if (!isValidEmail(email)) {
      setEntryEmailError('Enter a valid email address.');
      return;
    }
    if (entryInstructionsAdditionalEmails.includes(email)) {
      setEntryEmailError('This email is already in the list.');
      return;
    }
    const updated = [...entryInstructionsAdditionalEmails, email];
    dispatch(updateEntryInstructionEmailPrefs({
      hid: hostInfo._id,
      receiveEntryInstructionsEmails,
      entryInstructionsAdditionalEmails: updated,
    })).then((result) => {
      if (result.meta.requestStatus === 'fulfilled' && result.payload?.success) {
        setNewNotificationEmail('');
        setEntryEmailSuccess(result.payload.msg || 'Email added.');
      } else if (result.meta.requestStatus === 'rejected' && result.payload?.error) {
        setEntryEmailError(result.payload.error);
      }
    });
  };

  const handleRemoveNotificationEmail = (emailToRemove) => {
    setEntryEmailError('');
    setEntryEmailSuccess('');
    const updated = entryInstructionsAdditionalEmails.filter((e) => e !== emailToRemove);
    dispatch(updateEntryInstructionEmailPrefs({
      hid: hostInfo._id,
      receiveEntryInstructionsEmails,
      entryInstructionsAdditionalEmails: updated,
    })).then((result) => {
      if (result.meta.requestStatus === 'fulfilled' && result.payload?.success) {
        setEntryEmailSuccess(result.payload.msg || 'Email removed.');
      } else if (result.meta.requestStatus === 'rejected' && result.payload?.error) {
        setEntryEmailError(result.payload.error);
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 relative overflow-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-primary-500/5 to-purple-500/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-gradient-to-tr from-blue-500/5 to-primary-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 right-1/4 w-[300px] h-[300px] bg-gradient-to-r from-purple-500/3 to-pink-500/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <HostNav hide={true} />

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Integrations</h1>
              <p className="text-gray-500 mt-1">Connect your property management software to sync your properties</p>
            </div>
            <Link
              to="/properties"
              className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:-translate-y-0.5 font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Properties</span>
            </Link>
          </div>
        </div>

        <div className="space-y-6">
          {/* Guesty */}
          {showGuestyCard && (
          <PMSStatusCard
            title="Guesty Integration"
            status={isGuestyConnected ? 'connected' : 'disconnected'}
            isConnected={isGuestyConnected}
            onConnect={() => setShowGuestyForm(true)}
            onDisconnect={handleGuestyDisconnect}
            onSync={isGuestyConnected ? handleGuestySync : null}
            loading={loading}
            gradient="from-indigo-500 to-blue-600"
            icon={
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          >
            {isGuestyConnected ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50/80 to-emerald-50/60 backdrop-blur-sm rounded-xl p-4 border border-green-200/50">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-green-800 text-sm">Connection Active</h4>
                  </div>
                  <div className="text-sm text-green-700 space-y-1">
                    <div className="flex items-center justify-between bg-white/50 rounded-lg px-3 py-2">
                      <span className="text-xs font-medium text-green-600">Client ID</span>
                      <span className="text-xs font-mono font-bold text-green-800">{hostInfo.guesty_client_id?.slice(0, 8)}...</span>
                    </div>
                    <div className="flex items-center justify-between bg-white/50 rounded-lg px-3 py-2">
                      <span className="text-xs font-medium text-green-600">Client Secret</span>
                      <span className="text-xs font-mono font-bold text-green-800">{hostInfo.guesty_client_secret?.slice(0, 4)}******</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500">Your account is linked to Guesty. Sync properties anytime.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/60 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-gray-800 text-sm">Connect to Guesty</h4>
                  </div>
                  <p className="text-xs text-gray-600">Enter your Guesty API token to connect and sync your properties automatically.</p>
                </div>
                {showGuestyForm && (
                  <div className="space-y-4 bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-gray-200/30">
                    <APIKeyInput
                      label="Guesty API Token"
                      value={guestyToken}
                      onChange={(e) => setGuestyToken(e.target.value)}
                      placeholder="Enter your Guesty API token"
                    />
                    <div className="flex space-x-3">
                      <button
                        onClick={handleGuestyConnect}
                        disabled={loading}
                        className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 font-medium text-sm disabled:opacity-50 shadow-md shadow-primary-500/20"
                      >
                        {loading ? 'Connecting...' : 'Connect'}
                      </button>
                      <button
                        onClick={() => { setShowGuestyForm(false); setGuestyToken(''); }}
                        className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium text-sm border border-gray-200/60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </PMSStatusCard>
          )}

          {/* Streamline */}
          {showStreamlineCard && (
          <PMSStatusCard
            title="Streamline Integration"
            status={isStreamlineConnected ? 'connected' : needsStreamlineKeys ? 'needs-setup' : 'disconnected'}
            isConnected={isStreamlineConnected}
            onConnect={() => setShowStreamlineForm(true)}
            onDisconnect={handleStreamlineDisconnect}
            onSync={isStreamlineConnected ? handleStreamlineSync : null}
            loading={loading}
            gradient="from-cyan-500 to-teal-600"
            icon={
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }
          >
            {isStreamlineConnected ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50/80 to-emerald-50/60 backdrop-blur-sm rounded-xl p-4 border border-green-200/50">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-green-800 text-sm">Connection Active</h4>
                  </div>
                  <div className="text-sm text-green-700 space-y-1">
                    <div className="flex items-center justify-between bg-white/50 rounded-lg px-3 py-2">
                      <span className="text-xs font-medium text-green-600">API Key</span>
                      <span className="text-xs font-mono font-bold text-green-800">{hostInfo.streamline_api_key?.slice(0, 8)}...</span>
                    </div>
                    <div className="flex items-center justify-between bg-white/50 rounded-lg px-3 py-2">
                      <span className="text-xs font-medium text-green-600">API Secret</span>
                      <span className="text-xs font-mono font-bold text-green-800">{hostInfo.streamline_api_secret?.slice(0, 4)}******</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500">Your account is linked to Streamline. Sync properties anytime.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/60 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-gray-800 text-sm">
                      {needsStreamlineKeys ? 'Complete Streamline Setup' : 'Connect to Streamline'}
                    </h4>
                  </div>
                  <p className="text-xs text-gray-600">
                    {needsStreamlineKeys
                      ? 'You are registered as a Streamline host but need to provide your API credentials to sync properties.'
                      : 'Enter your Streamline API credentials to connect and sync your properties automatically.'
                    }
                  </p>
                </div>
                {showStreamlineForm && (
                  <div className="space-y-4 bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-gray-200/30">
                    <APIKeyInput
                      label="Streamline API Key"
                      value={streamlineApiKey}
                      onChange={(e) => setStreamlineApiKey(e.target.value)}
                      placeholder="Enter your Streamline API key"
                    />
                    <APIKeyInput
                      label="Streamline API Secret"
                      value={streamlineApiSecret}
                      onChange={(e) => setStreamlineApiSecret(e.target.value)}
                      placeholder="Enter your Streamline API secret"
                      type="password"
                    />
                    <div className="flex space-x-3">
                      <button
                        onClick={handleStreamlineConnect}
                        disabled={loading}
                        className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 font-medium text-sm disabled:opacity-50 shadow-md shadow-primary-500/20"
                      >
                        {loading ? 'Connecting...' : 'Connect'}
                      </button>
                      <button
                        onClick={() => { setShowStreamlineForm(false); setStreamlineApiKey(''); setStreamlineApiSecret(''); }}
                        className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium text-sm border border-gray-200/60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </PMSStatusCard>
          )}

          {/* Hospitable */}
          {showHospitableCard && (
          <PMSStatusCard
            title="Hospitable Integration"
            status={isHospitableConnected ? 'connected' : 'disconnected'}
            isConnected={isHospitableConnected}
            onConnect={handleHospitableConnect}
            onDisconnect={handleHospitableDisconnect}
            onSync={isHospitableConnected ? handleHospitableSync : null}
            loading={loading}
            gradient="from-rose-500 to-red-600"
            icon={
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M8 12h8m-8 5h8M5 4h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" />
              </svg>
            }
          >
            {isHospitableConnected ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50/80 to-emerald-50/60 backdrop-blur-sm rounded-xl p-4 border border-green-200/50">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-green-800 text-sm">Connection Active</h4>
                  </div>
                  <div className="text-sm text-green-700 space-y-1">
                    <div className="flex items-center justify-between bg-white/50 rounded-lg px-3 py-2">
                      <span className="text-xs font-medium text-green-600">Account ID</span>
                      <span className="text-xs font-mono font-bold text-green-800">{hostInfo?.hospitableAccountId || 'Linked'}</span>
                    </div>
                    <div className="flex items-center justify-between bg-white/50 rounded-lg px-3 py-2">
                      <span className="text-xs font-medium text-green-600">Connected</span>
                      <span className="text-xs font-bold text-green-800">
                        {hostInfo?.hospitableConnectedAt ? new Date(hostInfo.hospitableConnectedAt).toLocaleDateString() : 'Recently'}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500">Your account is linked to Hospitable. Sync properties anytime.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/60 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-rose-500 to-red-500 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-gray-800 text-sm">Connect to Hospitable</h4>
                  </div>
                  <p className="text-xs text-gray-600">Use secure OAuth to connect Hospitable and sync your properties automatically.</p>
                </div>
              </div>
            )}
          </PMSStatusCard>
          )}

          {!showGuestyCard && !showStreamlineCard && !showHospitableCard && (
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 p-6">
              <p className="text-sm text-gray-600">
                No integration is configured for your selected PMS. You can update this in{' '}
                <Link to={`/settings/${hostInfo?._id}`} className="font-medium text-primary-600 hover:text-primary-700">
                  Profile Settings
                </Link>.
              </p>
            </div>
          )}

          {/* Entry Instruction Emails */}
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 overflow-hidden hover:shadow-lg transition-all duration-300">
            <div className="p-6 border-b border-gray-100/50">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Entry Instruction Emails</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Get Notified When Orders Need Entry Instructions and Delivery Time</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <label className="text-sm font-medium text-gray-700">Receive Entry Instruction Emails</label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={receiveEntryInstructionsEmails}
                  onClick={handleToggleEntryInstructions}
                  disabled={loadingFlags}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                    receiveEntryInstructionsEmails ? 'bg-primary-500' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                      receiveEntryInstructionsEmails ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {receiveEntryInstructionsEmails && (
                <div className="rounded-xl border border-gray-200/60 bg-gray-50/50 p-4 space-y-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Additional Notification Emails</p>
                  <ul className="space-y-2">
                    {entryInstructionsAdditionalEmails.map((email) => (
                      <li
                        key={email}
                        className="flex items-center justify-between gap-2 py-2 px-3 bg-white rounded-lg border border-gray-200/60"
                      >
                        <span className="text-sm text-gray-900 truncate">{email}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveNotificationEmail(email)}
                          disabled={loadingFlags}
                          className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          aria-label={`Remove ${email}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="email"
                      value={newNotificationEmail}
                      onChange={(e) => setNewNotificationEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddNotificationEmail())}
                      placeholder="Add Another Email Address"
                      disabled={loadingFlags}
                      className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-gray-200/60 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={handleAddNotificationEmail}
                      disabled={loadingFlags}
                      className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                    >
                      {loadingFlags ? 'Saving…' : 'Add Email'}
                    </button>
                  </div>
                </div>
              )}

              {entryEmailError && <p className="text-sm text-red-600">{entryEmailError}</p>}
              {entryEmailSuccess && <p className="text-sm text-green-600">{entryEmailSuccess}</p>}

              <p className="text-xs text-gray-500">
                You Can Also Manage This in <Link to={`/settings/${hostInfo?._id}`} className="font-medium text-primary-600 hover:text-primary-700">Profile Settings</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default IntegrationsScreen;
